/**
 * Webhooks — tenant-scoped webhook management.
 *
 * Thin wrapper around the developer webhook APIs — surfaces only the
 * essentials needed for institution admins.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Webhooks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState("");
  const [events, setEvents] = useState(["vlk_inst.suspended", "vlk_inst.reinstated"]);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/developer/webhooks")
      .then((r) => setRows(r.data?.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!newUrl.startsWith("http")) {
      toast.error("URL must start with http/https");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/developer/webhooks", { url: newUrl, events });
      setReveal(r.data);
      setNewUrl("");
      load();
    } catch (e) {
      toast.error("Create failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (id) => {
    if (!window.confirm("Rotate signing secret? Callers using the old secret will start failing.")) return;
    try {
      const r = await api.post(`/developer/webhooks/${id}/rotate-secret`, {});
      setReveal({ ...r.data, rotated: true });
      load();
    } catch (e) {
      toast.error("Rotate failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="institution-page-webhooks">
      <div>
        <h1 className="text-xl font-semibold">Webhooks</h1>
        <p className="text-sm text-muted-foreground">
          Deliveries are HMAC-signed with a secret that is shown exactly once at creation/rotation.
        </p>
      </div>

      {reveal && (
        <div className="border-2 border-yellow-500/40 bg-yellow-500/10 rounded p-4 space-y-2"
             data-testid="webhook-reveal-card">
          <div className="text-xs uppercase tracking-widest text-yellow-700 dark:text-yellow-500">
            One-time reveal — copy now, this cannot be shown again
          </div>
          <div className="text-xs">
            <div className="text-muted-foreground">Webhook ID</div>
            <div className="font-mono">{reveal.webhook_id}</div>
          </div>
          <div className="text-xs">
            <div className="text-muted-foreground">Signing secret</div>
            <div className="font-mono break-all">{reveal.signing_secret}</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setReveal(null)}
                  data-testid="webhook-reveal-dismiss">Dismiss</Button>
        </div>
      )}

      <div className="border rounded p-4 space-y-3">
        <div className="text-sm font-semibold">Register a webhook</div>
        <Input placeholder="https://your-backend.example/vlk-webhook"
               value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
               data-testid="webhook-url-input" />
        <div className="text-xs text-muted-foreground">
          Subscribed events: <code className="font-mono">{events.join(", ")}</code>
        </div>
        <Button onClick={create} disabled={busy || !newUrl} data-testid="webhook-create-btn">
          {busy ? "Registering…" : "Register"}
        </Button>
      </div>

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="webhooks-table">
          <thead className="bg-muted/30 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">URL</th>
              <th className="text-left p-3">Events</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No webhooks registered.</td></tr>
            )}
            {rows.map((w) => (
              <tr key={w.webhook_id} className="border-t">
                <td className="p-3 font-mono text-xs">{w.url}</td>
                <td className="p-3 text-xs">{(w.events || []).join(", ")}</td>
                <td className="p-3">
                  <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${
                    w.status === "ACTIVE" ? "bg-green-500/15 text-green-700 dark:text-green-500"
                                          : "bg-muted text-muted-foreground"
                  }`}>{w.status}</span>
                </td>
                <td className="p-3">
                  <Button size="sm" variant="outline" onClick={() => rotate(w.webhook_id)}
                          data-testid={`webhook-rotate-${w.webhook_id}`}>
                    Rotate secret
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
